import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import { ZapIcon } from "lucide-react";
import { type ReactNode, useId } from "react";

import { Button } from "@/shared/ui/button";

import { useIngestStatus } from "../../api/useIngestStatus";
import { useMatches } from "../../api/useMatches";
import { isNormalGame } from "../../lib/isNormalGame";

// Дата — в скобках: короткий месяц на русском сам с точкой («31 окт.»).
const LATEST = msg({
  message:
    "Your latest match ({day}) is already analyzed. Without the app, new ones show up 2–3 days after the game.",
});

type NoNewMatchesProps = {
  // Второе действие рядом с главной кнопкой: что делать, пока новых матчей нет.
  secondaryAction?: ReactNode;
};

// «Новых матчей пока нет» по макету «15г» (`design/prototype/ReturnNoMatches.dc.html`):
// последний обычный матч уже разобран, а программы Deadlock API Ingest нет — новые матчи
// придут только через 2–3 дня. Карточка объясняет это и ведёт на «Матчи сразу»; второе
// действие задаёт маршрут (тренировка). Пока список или статус грузятся, статус не
// загрузился или ещё ни разу не проверен, есть новый неразобранный матч или программа
// подключена — ничего.
export const NoNewMatches = ({ secondaryAction }: NoNewMatchesProps) => {
  const { i18n } = useLingui();
  const headingId = useId();
  const { query, items } = useMatches();
  const status = useIngestStatus();
  // Статус не загрузился или ещё ни разу не проверен — молчим: программа может стоять.
  const ingest = status.data;
  if (
    query.isPending ||
    ingest === undefined ||
    ingest.active ||
    (ingest.pending && ingest.checked_at === null)
  ) {
    return null;
  }
  const latest = items.find(isNormalGame);
  if (latest?.analysis_status !== "done") {
    return null;
  }
  const day = new Intl.DateTimeFormat(i18n.locale, { day: "numeric", month: "short" }).format(
    new Date(latest.started_at),
  );
  const steps = [
    <Trans key="install">Install the app</Trans>,
    <Trans key="play">Play as usual</Trans>,
    <Trans key="here">The match is here in a few minutes</Trans>,
  ];
  return (
    <section
      aria-labelledby={headingId}
      data-block="no-new-matches"
      className="flex flex-col gap-4 rounded-[14px] border border-border-strong bg-surface px-6 py-5"
    >
      <div className="flex flex-col gap-1">
        <h2 id={headingId} className="text-xl font-semibold">
          <Trans>No new matches yet</Trans>
        </h2>
        <p className="text-muted">{i18n._({ ...LATEST, values: { day } })}</p>
      </div>
      <ol className="grid grid-cols-[repeat(auto-fit,minmax(min(170px,100%),1fr))] gap-2">
        {steps.map((step, index) => (
          <li
            key={step.key}
            className="flex items-center gap-2.5 rounded-[10px] bg-surface-raised px-3 py-2.5 text-sm"
          >
            <span
              aria-hidden="true"
              className="inline-flex size-[26px] shrink-0 items-center justify-center rounded-full border border-border-strong font-mono text-[13px] font-semibold text-accent"
            >
              {index + 1}
            </span>
            {step}
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap gap-2.5">
        <Button asChild size="lg" className="h-11">
          <Link to="/fresh-matches" className="no-underline">
            <ZapIcon aria-hidden="true" />
            <Trans>Get matches right away</Trans>
          </Link>
        </Button>
        {secondaryAction}
      </div>
    </section>
  );
};
